import type {
  Question,
  QuestionAnswer,
} from '@/features/jobs/questions/questions'
import type { RoutingContextSource } from '@/features/legends/data-sources/data-sources'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import type { OrganizerBinding } from '@/features/legends/legend-engine'
import { maybeParseDate } from '@/utils/date-util'

/**
 * A sample answer, as the engine takes it: a boolean, an option ID, a list of
 * option IDs, a string, a number or an ISO date.
 */
export interface PreviewResponse {
  id: string
  answer: unknown
}

/**
 * Converts an answer to the engine's representation. Documents can't be
 * answered without a job, so they have none.
 *
 * @param answer
 */
export function toEngineAnswer(answer: QuestionAnswer): unknown {
  switch (answer.type) {
    case 'Bool':
    case 'SingleSelectAnswer':
    case 'MultiSelectAnswer':
    case 'Text':
    case 'Numeric':
      return answer.value
    case 'DateTime':
      return answer.value.toISOString()
    case 'Documents':
    case 'Unknown':
      return undefined
  }
}

/**
 * Gives the question the answer in the engine's representation. An answer
 * that doesn't fit the question, like one stored for an earlier version of
 * it, is dropped.
 *
 * @param question
 * @param answer
 */
export function withEngineAnswer(
  question: Question,
  answer: unknown,
): Question {
  switch (question.type) {
    case 'Bool':
      return {
        ...question,
        answer:
          typeof answer === 'boolean' ? { type: 'Bool', value: answer } : null,
      }
    case 'Select':
      return {
        ...question,
        answer: question.multi
          ? isStringArray(answer)
            ? { type: 'MultiSelectAnswer', value: answer }
            : null
          : typeof answer === 'string'
            ? { type: 'SingleSelectAnswer', value: answer }
            : null,
      }
    case 'Text':
      return {
        ...question,
        answer:
          typeof answer === 'string' ? { type: 'Text', value: answer } : null,
      }
    case 'Numeric':
      return {
        ...question,
        answer:
          typeof answer === 'number'
            ? { type: 'Numeric', value: answer }
            : null,
      }
    case 'DateTime': {
      const value = typeof answer === 'string' ? maybeParseDate(answer) : null
      return {
        ...question,
        answer: value ? { type: 'DateTime', value } : null,
      }
    }
    case 'Documents':
    case 'Breaker':
    case 'Unknown':
      return question
  }
}

/**
 * Reads stored responses, dropping anything that isn't one.
 *
 * @param stored
 */
export function parseStoredResponses(stored: unknown): PreviewResponse[] {
  if (!Array.isArray(stored)) {
    return []
  }
  return stored.flatMap((entry: unknown) =>
    typeof entry === 'object' &&
    entry !== null &&
    'id' in entry &&
    typeof entry.id === 'string' &&
    'answer' in entry
      ? [{ id: entry.id, answer: entry.answer }]
      : [],
  )
}

/**
 * A question for the value the organizer collects for the binding. Lists are
 * entered as comma-separated text.
 *
 * @param binding
 * @param elementId The ID of the collected value element.
 */
export function mapCollectedValueBindingToQuestion(
  binding: OrganizerBinding,
  elementId: string,
): Question {
  const props = {
    id: binding.id,
    title: `${binding.displayName} (${elementId})`,
    answer: null,
  }
  if (Types.isNumberType(binding.valueType)) {
    return {
      ...props,
      type: 'Numeric',
      format: { type: 'Decimal', places: 2 },
    }
  }
  if (Types.isBoolType(binding.valueType)) {
    return { ...props, type: 'Bool' }
  }
  if (Types.isListType(binding.valueType)) {
    return { ...props, type: 'Text', description: 'Comma-separated list.' }
  }
  return { ...props, type: 'Text' }
}

/**
 * Converts the answer for a collected value question to the binding's value.
 *
 * @param answer
 * @param valueType The binding's value type.
 */
export function parseCollectedValue(answer: unknown, valueType: RynoType) {
  if (!Types.isListType(valueType)) {
    return answer
  }
  const items = (typeof answer === 'string' ? answer : '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
  return Types.isNumberType(valueType.elementType) ? items.map(Number) : items
}

/**
 * A question for the value of a routing context data source, which comes
 * from the job's client or the platform once the job is submitted. A source
 * of a type the builder doesn't know is a heading that says so.
 *
 * @param source
 * @param id The source's ID.
 */
export function mapRoutingContextSourceToQuestion(
  source: RoutingContextSource,
  id: string,
): Question {
  switch (source.type) {
    case 'Unknown':
      return {
        id,
        type: 'Breaker',
        title: 'Unknown data source type',
        answer: null,
      }
    case 'UserWorkRequestCount':
      return {
        id,
        type: 'Numeric',
        title: `${id} (User Work Request Count)`,
        format: { type: 'Integer' },
        answer: null,
      }
    case 'ServiceItemPresence':
      return {
        id,
        type: 'Bool',
        title: `${id} (Service Item Presence)`,
        answer: null,
      }
  }
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string')
}
